"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

export interface CarouselProps {
  images: string[];
  autoplay?: boolean;
  interval?: number;
  showDots?: boolean;
  showArrows?: boolean;
  aspectRatio?: "video" | "wide" | "square" | "hero" | "auto";
  className?: string;
  imageClassName?: string;
  alt?: string;
  /** Fixed height (e.g., "300px", "50vh") - when set, aspect ratio is ignored */
  height?: string;
  /** Fixed width (e.g., "100%", "800px") - defaults to 100% */
  width?: string;
  /** How images should fit: "cover" fills container, "contain" shows full image */
  fit?: "cover" | "contain";
  /** Number of slides visible at once (slider mode) */
  slidesPerView?: number;
  /** Gap between slides in pixels (slider mode) */
  gap?: number;
  /** Whether to loop back to the beginning when reaching the end */
  loop?: boolean;
  /** Focus mode: center slide is larger, side slides are smaller with smooth transitions */
  focus?: boolean;
}

/**
 * Carousel Component
 *
 * A flexible image carousel with support for:
 * - Single image mode (traditional carousel)
 * - Slider mode (multiple visible images)
 * - Configurable dimensions (height, width)
 * - Autoplay with pause on hover
 * - Touch/swipe navigation (via CSS scroll-snap)
 */
export function Carousel({
  images,
  autoplay = false,
  interval = 5000,
  showDots = true,
  showArrows = true,
  aspectRatio = "video",
  className,
  imageClassName,
  alt = "Carousel image",
  height,
  width = "100%",
  fit = "cover",
  slidesPerView = 1,
  gap = 16,
  loop = true,
  focus = false,
}: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isSliderMode = slidesPerView > 1;

  const aspectClasses: Record<string, string> = {
    video: "aspect-video",
    wide: "aspect-[21/9]",
    square: "aspect-square",
    hero: "aspect-[3/1]",
    auto: "",
  };

  // In focus+loop mode, currentIndex represents the focused image (0 to images.length-1)
  // In regular mode, it represents the first visible slide position
  const isFocusLoopMode = focus && loop && isSliderMode;

  // Calculate max index based on mode
  const maxIndex = isFocusLoopMode
    ? images.length - 1  // Can focus any image
    : Math.max(0, images.length - slidesPerView);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => {
      if (isFocusLoopMode) {
        // Wrap around in focus+loop mode
        return (prev + 1) % images.length;
      }
      if (prev >= maxIndex) {
        return loop ? 0 : prev;
      }
      return prev + 1;
    });
  }, [maxIndex, loop, isFocusLoopMode, images.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => {
      if (isFocusLoopMode) {
        // Wrap around in focus+loop mode
        return (prev - 1 + images.length) % images.length;
      }
      if (prev <= 0) {
        return loop ? maxIndex : prev;
      }
      return prev - 1;
    });
  }, [maxIndex, loop, isFocusLoopMode, images.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(Math.min(index, maxIndex));
  }, [maxIndex]);

  // Autoplay effect
  useEffect(() => {
    if (!autoplay || isPaused || images.length <= slidesPerView) return;

    const timer = setInterval(goToNext, interval);
    return () => clearInterval(timer);
  }, [autoplay, interval, isPaused, goToNext, images.length, slidesPerView]);

  // Scroll to current index in slider mode
  useEffect(() => {
    if (scrollContainerRef.current && isSliderMode) {
      const slideWidth = scrollContainerRef.current.scrollWidth / images.length;
      scrollContainerRef.current.scrollTo({
        left: currentIndex * slideWidth,
        behavior: "smooth",
      });
    }
  }, [currentIndex, isSliderMode, images.length]);

  if (!images || images.length === 0) {
    return null;
  }

  // Container style
  const containerStyle: React.CSSProperties = {
    width,
    maxWidth: "100%",
    overflow: "hidden",
  };

  if (height) {
    containerStyle.height = height;
  }

  // Single image - no controls needed
  if (images.length === 1 && !isSliderMode) {
    return (
      <div
        className={cn("relative rounded-lg overflow-hidden", !height && aspectClasses[aspectRatio], className)}
        style={containerStyle}
      >
        <Image
          src={images[0]}
          alt={alt}
          fill
          className={cn(fit === "contain" ? "object-contain" : "object-cover", imageClassName)}
          unoptimized
        />
      </div>
    );
  }

  // Slider mode - show multiple images at once
  if (isSliderMode) {
    // Calculate the width each slide should take (accounting for gaps)
    // Total gap space = gap * (slidesPerView - 1)
    // Each slide width = (100% - total gap space) / slidesPerView
    const totalGapSpace = gap * (slidesPerView - 1);

    // For focus mode, the center position is in focus
    const centerOffset = Math.floor(slidesPerView / 2);

    // In focus+loop mode, we render only visible slides dynamically
    // In regular mode, we render all slides and translate
    if (isFocusLoopMode) {
      // Generate the visible slide indices (wrapping around)
      const visibleSlides: { imageIndex: number; position: number }[] = [];
      for (let pos = 0; pos < slidesPerView; pos++) {
        // Calculate offset from center (-1, 0, 1 for 3 slides)
        const offsetFromCenter = pos - centerOffset;
        // Calculate the actual image index, wrapping around
        const imageIndex = (currentIndex + offsetFromCenter + images.length) % images.length;
        visibleSlides.push({ imageIndex, position: pos });
      }

      // Calculate scale and opacity based on position (center = 0)
      const getSlideStyle = (position: number): React.CSSProperties => {
        const distanceFromCenter = Math.abs(position - centerOffset);

        // Scale: 1.0 for center, decreasing for others
        const scale = distanceFromCenter === 0 ? 1 : Math.max(0.75, 1 - distanceFromCenter * 0.12);

        // Opacity: 1.0 for center, decreasing for others
        const opacity = distanceFromCenter === 0 ? 1 : Math.max(0.5, 1 - distanceFromCenter * 0.25);

        return {
          transform: `scale(${scale})`,
          opacity,
          transition: "transform 0.4s ease-out, opacity 0.4s ease-out",
          zIndex: 10 - distanceFromCenter,
        };
      };

      return (
        <div
          className={cn("relative w-full", className)}
          style={containerStyle}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          role="region"
          aria-label="Image slider"
          aria-roledescription="carousel"
        >
          {/* Slides Container - no translation needed, we render only visible slides */}
          <div
            ref={scrollContainerRef}
            className="w-full overflow-hidden"
            style={{
              height: height || "auto",
            }}
          >
            <div
              className="flex items-center justify-center"
              style={{
                gap: `${gap}px`,
                minHeight: height || "200px",
              }}
            >
              {visibleSlides.map(({ imageIndex, position }) => (
                <div
                  key={`${currentIndex}-${position}`}
                  className={cn(
                    "relative flex-shrink-0 rounded-lg overflow-hidden",
                    !height && aspectClasses[aspectRatio]
                  )}
                  style={{
                    width: `calc((100% - ${totalGapSpace}px) / ${slidesPerView})`,
                    height: height || undefined,
                    minHeight: height || "200px",
                    ...getSlideStyle(position),
                  }}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`Slide ${imageIndex + 1} of ${images.length}`}
                >
                  <Image
                    src={images[imageIndex]}
                    alt={`${alt} ${imageIndex + 1}`}
                    fill
                    className={cn(fit === "contain" ? "object-contain" : "object-cover", imageClassName)}
                    unoptimized
                    priority={position === centerOffset}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Previous/Next Arrows - always enabled in focus+loop mode */}
          {showArrows && images.length > 1 && (
            <>
              <button
                onClick={goToPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                aria-label="Previous slide"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                aria-label="Next slide"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Dot Indicators */}
          {showDots && images.length > 1 && (
            <div
              className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-2"
              role="tablist"
              aria-label="Carousel navigation"
            >
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={cn(
                    "w-2.5 h-2.5 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black/50",
                    index === currentIndex
                      ? "bg-white scale-110"
                      : "bg-white/50 hover:bg-white/75"
                  )}
                  role="tab"
                  aria-selected={index === currentIndex}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    // Regular slider mode (non-focus or non-loop)
    const focusedIndex = currentIndex + centerOffset;

    // Calculate scale and opacity for focus mode
    const getSlideStyle = (index: number): React.CSSProperties => {
      if (!focus) {
        return {};
      }

      // Distance from the focused slide
      const distance = Math.abs(index - focusedIndex);

      // Scale: 1.0 for focused, decreasing for others
      const scale = distance === 0 ? 1 : Math.max(0.75, 1 - distance * 0.12);

      // Opacity: 1.0 for focused, decreasing for others
      const opacity = distance === 0 ? 1 : Math.max(0.5, 1 - distance * 0.25);

      return {
        transform: `scale(${scale})`,
        opacity,
        transition: "transform 0.4s ease-out, opacity 0.4s ease-out",
        zIndex: 10 - distance,
      };
    };

    return (
      <div
        className={cn("relative w-full", className)}
        style={containerStyle}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        role="region"
        aria-label="Image slider"
        aria-roledescription="carousel"
      >
        {/* Slides Container */}
        <div
          ref={scrollContainerRef}
          className="w-full overflow-hidden"
          style={{
            height: height || "auto",
          }}
        >
          <div
            className="flex transition-transform duration-500 ease-out items-center"
            style={{
              gap: `${gap}px`,
              transform: `translateX(calc(-${currentIndex} * (100% / ${slidesPerView} + ${gap}px - ${totalGapSpace / slidesPerView}px)))`,
              minHeight: height || "200px",
            }}
          >
            {images.map((src, index) => (
              <div
                key={index}
                className={cn(
                  "relative flex-shrink-0 rounded-lg overflow-hidden",
                  !height && aspectClasses[aspectRatio]
                )}
                style={{
                  width: `calc((100% - ${totalGapSpace}px) / ${slidesPerView})`,
                  height: height || undefined,
                  minHeight: height || "200px",
                  ...getSlideStyle(index),
                }}
                role="group"
                aria-roledescription="slide"
                aria-label={`Slide ${index + 1} of ${images.length}`}
              >
                <Image
                  src={src}
                  alt={`${alt} ${index + 1}`}
                  fill
                  className={cn(fit === "contain" ? "object-contain" : "object-cover", imageClassName)}
                  unoptimized
                  priority={index < slidesPerView}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Previous/Next Arrows */}
        {showArrows && images.length > slidesPerView && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Previous slide"
              disabled={!loop && currentIndex === 0}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Next slide"
              disabled={!loop && currentIndex >= maxIndex}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Dot Indicators */}
        {showDots && images.length > slidesPerView && (
          <div
            className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-2"
            role="tablist"
            aria-label="Carousel navigation"
          >
            {Array.from({ length: maxIndex + 1 }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={cn(
                  "w-2.5 h-2.5 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black/50",
                  index === currentIndex
                    ? "bg-white scale-110"
                    : "bg-white/50 hover:bg-white/75"
                )}
                role="tab"
                aria-selected={index === currentIndex}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Standard carousel mode - single image at a time with fade transition
  return (
    <div
      className={cn("relative overflow-hidden rounded-lg", className)}
      style={containerStyle}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      role="region"
      aria-label="Image carousel"
      aria-roledescription="carousel"
    >
      {/* Slides Container */}
      <div className={cn("relative w-full h-full", !height && aspectClasses[aspectRatio])} style={height ? { height } : undefined}>
        {images.map((src, index) => (
          <div
            key={index}
            className={cn(
              "absolute inset-0 transition-opacity duration-500",
              index === currentIndex ? "opacity-100 z-10" : "opacity-0 z-0"
            )}
            role="group"
            aria-roledescription="slide"
            aria-label={`Slide ${index + 1} of ${images.length}`}
            aria-hidden={index !== currentIndex}
          >
            <Image
              src={src}
              alt={`${alt} ${index + 1}`}
              fill
              className={cn(fit === "contain" ? "object-contain" : "object-cover", imageClassName)}
              unoptimized
              priority={index === 0}
            />
          </div>
        ))}
      </div>

      {/* Previous/Next Arrows */}
      {showArrows && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Previous slide"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Next slide"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Dot Indicators */}
      {showDots && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-2"
          role="tablist"
          aria-label="Carousel navigation"
        >
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={cn(
                "w-2.5 h-2.5 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black/50",
                index === currentIndex
                  ? "bg-white scale-110"
                  : "bg-white/50 hover:bg-white/75"
              )}
              role="tab"
              aria-selected={index === currentIndex}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
